import { AppShell, Container, Group, Text } from "@mantine/core";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { IconHome, IconSettings } from "@tabler/icons-react";
import {
  type ReactPlugin,
  useAppInsightsContext
} from "@microsoft/applicationinsights-react-js";
import { RouteErrorBoundary } from "./-RouteErrorBoundary";
import type { QueryClient } from "@tanstack/react-query";
import type { UserClaim } from "@/models";
import HeaderComponent from "@/components/HeaderComponent";
import Breadcrumb from "@/components/Breadcrumb";
import Sidebar from "@/components/Sidebar";
import { syncRecordings } from "@/sync/recordingsSync";
import useApi from "@/api/axios";
import { localRecordingStorage } from "@/localDb/localRecordingStorage";
import { canViewCommunitySettings } from "@/security/logic";

export interface RouterContext {
  appInsightsReactPlugin?: ReactPlugin;
  queryClient: QueryClient;
  apiHook: ReturnType<typeof useApi>;
  userClaim?: UserClaim;
}

const RootLayout = () => {
  const { userClaim } = Route.useRouteContext();
  const { secureAxios } = useApi();
  const appInsights = useAppInsightsContext();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const toggleSidebarCollapsed = () => setSidebarCollapsed((o) => !o);

  const [dbReady, setDbReady] = useState(false);

  const canAccessSettings = canViewCommunitySettings(userClaim, "somewhere");

  useEffect(() => {
    async function initDB() {
      try {
        // Test the database by performing a simple operation
        // This will trigger the database to open and catch any initialization errors
        await localRecordingStorage.getUnsynchedRecordingSessions();
        console.log("Database initialized successfully");
        setDbReady(true);
      } catch (error) {
        console.error("Failed to initialize IndexedDB", error);
      }
    }
    void initDB();
  }, []);

  useEffect(() => {
    async function uploadRecordingsToServer() {
      await syncRecordings(secureAxios, appInsights);

      setTimeout(() => {
        uploadRecordingsToServer().catch((err) => {
          console.error(err);
        });
      }, 5000);
    }
    if (dbReady) {
      uploadRecordingsToServer().catch((err) => {
        console.error(err);
      });
    }
  }, [dbReady, appInsights, secureAxios]);

  return (
    <>
      <AppShell
        withBorder={false}
        padding="md"
        header={{ height: 60 }}
        footer={{ height: 40 }}
        navbar={{
          width: sidebarCollapsed ? 60 : 170,
          breakpoint: 0,
          collapsed: { mobile: sidebarCollapsed }
        }}
      >
        <HeaderComponent isCollapsed={sidebarCollapsed} />
        <AppShell.Navbar bg="optimaBlue.9">
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapsed}
            navItems={[
              { label: "Prospects", to: "/prospects", icon: IconHome },
              ...(canAccessSettings
                ? [{ label: "Settings", to: "/settings", icon: IconSettings }]
                : [])
            ]}
          />
        </AppShell.Navbar>
        <AppShell.Main>
          <Container strategy="grid" size={1150}>
            <Breadcrumb />
            <Outlet />
          </Container>
        </AppShell.Main>
        <AppShell.Footer withBorder zIndex={300}>
          <Group align="center" justify="space-between" h="100%" px={30}>
            <Text size="xs" c="warmGray.7">
              © 2025 Optima Living. All rights reserved.
            </Text>
            <Text size="xs" c="warmGray.7">
              Get help
            </Text>
          </Group>
        </AppShell.Footer>
      </AppShell>
    </>
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RouteErrorBoundary
});
